import React, { useState } from 'react';
import type { ImageTag } from '../../services/api/imageTagService';
import type { BannerTagsController } from '../../pages/ads/components/useBannerTags';
import ImageTagPicker, { type TagCheckState } from './ImageTagPicker';
import TagPopover from './TagPopover';
import { tagColorClasses } from './imageTagColors';
import { TaskyIconButton } from '../tasky';

interface ImageTagMenuProps {
  controller: BannerTagsController;
  /** 付け外しの対象。1枚でも、まとめて選んだ複数枚でもよい */
  targets: { id: number; tags?: ImageTag[] }[];
  renderTrigger: React.ComponentProps<typeof TagPopover>['renderTrigger'];
}

/** 対象のバナーの全部・一部・どれにもそのタグが付いているか */
export const tagCheckState = (targets: { tags?: ImageTag[] }[], tagId: number): TagCheckState => {
  const tagged = targets.filter((target) => target.tags?.some((tag) => tag.id === tagId)).length;
  if (tagged === 0) return 'none';
  return tagged === targets.length ? 'all' : 'some';
};

/** 押したらすぐ付け外しするポップアップ（カードの「+」）。開くボタンの見た目は呼び出し側が決める */
export const ImageTagMenu: React.FC<ImageTagMenuProps> = ({ controller, targets, renderTrigger }) => {
  const [open, setOpen] = useState(false);
  const imageIds = targets.map((target) => target.id);
  const checkState = (tagId: number) => tagCheckState(targets, tagId);

  return (
    <TagPopover open={open} onOpenChange={setOpen} label="タグ" renderTrigger={renderTrigger}>
      <ImageTagPicker
        tags={controller.tags}
        checkState={checkState}
        onToggle={(tag, attach) => controller.assign(imageIds, attach ? { add: [tag.id] } : { remove: [tag.id] })}
        onCreate={async (name) => {
          const tag = await controller.createTag(name);
          if (tag) await controller.assign(imageIds, { add: [tag.id] });
        }}
        onUpdate={controller.updateTag}
        onDelete={(tag) => {
          // 確認ダイアログの上にポップアップが重ならないよう先に閉じる
          setOpen(false);
          void controller.deleteTag(tag);
        }}
      />
    </TagPopover>
  );
};

export const ImageTagChip: React.FC<{ tag: ImageTag; className?: string }> = ({ tag, className = '' }) => (
  <span
    className={`inline-flex max-w-full items-center truncate whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium leading-4 ${
      tagColorClasses(tag.color).chip
    } ${className}`}
  >
    {tag.name}
  </span>
);

interface ImageTagListProps {
  controller: BannerTagsController;
  image: { id: number; tags?: ImageTag[] };
  /** false なら付いているタグを見せるだけ（ABテストの候補選び中など） */
  editable?: boolean;
  className?: string;
}

/** カードと詳細に出すタグの並びと「+」 */
export const ImageTagList: React.FC<ImageTagListProps> = ({ controller, image, editable = true, className = '' }) => {
  if (!controller.enabled) return null;
  const tags = image.tags ?? [];
  if (!editable && tags.length === 0) return null;

  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`}>
      {tags.map((tag) => (
        <ImageTagChip key={tag.id} tag={tag} />
      ))}
      {editable && <ImageTagMenu
        controller={controller}
        targets={[image]}
        renderTrigger={({ ref, props }) => (
          <TaskyIconButton
            ref={ref}
            type="button"
            variant="ghost"
            size="sm"
            aria-label="タグを付ける"
            title="タグを付ける"
            className="h-5 w-5 flex-none border border-dashed border-gray-400 text-sm leading-none text-gray-500 hover:border-solid hover:border-primary hover:bg-transparent hover:text-primary"
            {...props}
          >
            +
          </TaskyIconButton>
        )}
      />}
    </div>
  );
};
